export interface CompareRowLike {
  key: string;
  displayName: string;
  aValue: number | null;
  /** Locked column values aligned with locked points order (B, C, D...). */
  lockedValues: (number | null)[];
}

export interface CompareSummary {
  increased: number;
  decreased: number;
  unchanged: number;
  largestUp: { name: string; pct: number } | null;
  largestDown: { name: string; pct: number } | null;
}

export const FLAT_PCT_THRESHOLD = 0.05;

/** Delta % of lockedValue relative to A baseline: ((lockedValue - A) / |A|) * 100 */
export function pctDelta(
  aValue: number | null,
  lockedValue: number | null,
): number | null {
  if (aValue === null || lockedValue === null) return null;
  if (aValue === 0) return null;
  return ((lockedValue - aValue) / Math.abs(aValue)) * 100;
}

/** Delta of a single locked column value relative to A (baseline). */
export function rowDeltaPct(
  aValue: number | null,
  lockedValue: number | null,
): number | null {
  return pctDelta(aValue, lockedValue);
}

export function isUnchanged(pct: number | null): boolean {
  if (pct === null) return false;
  return Math.abs(pct) < FLAT_PCT_THRESHOLD;
}

/** Aggregate counts/extremes across rows using primary locked column (B) vs A. */
export function computeSummary(rows: CompareRowLike[]): CompareSummary {
  let increased = 0;
  let decreased = 0;
  let unchanged = 0;
  let largestUp: { name: string; pct: number } | null = null;
  let largestDown: { name: string; pct: number } | null = null;

  for (const r of rows) {
    const primary = r.lockedValues[0] ?? null;
    const pct = rowDeltaPct(r.aValue, primary);
    if (pct === null) continue;
    if (isUnchanged(pct)) {
      unchanged++;
      continue;
    }
    if (pct > 0) {
      increased++;
      if (!largestUp || pct > largestUp.pct) {
        largestUp = { name: r.displayName, pct };
      }
    } else {
      decreased++;
      if (!largestDown || pct < largestDown.pct) {
        largestDown = { name: r.displayName, pct };
      }
    }
  }

  return { increased, decreased, unchanged, largestUp, largestDown };
}
